
import React from "react";
import { ApparelProduct } from "@/types";
import { useProductFiltering } from "@/hooks/useProductFiltering";
import { ProductFilterControls } from "./grid/ProductFilterControls";
import { ProductCard } from "./grid/ProductCard";
import { ProductListItem } from "./grid/ProductListItem";
import ViewSwitcher from "@/components/ui/view-switcher";
import { ConnectionErrorAlert } from "@/components/common/ConnectionErrorAlert";
import ErrorBoundary from "@/components/common/ErrorBoundary";

interface ApparelProductGridProps {
  products: ApparelProduct[];
  categories: string[];
  onEdit?: (product: ApparelProduct) => void;
  onDelete?: (product: ApparelProduct) => void;
  onSelect?: (product: ApparelProduct) => void;
  showAdminControls?: boolean;
}

const ApparelProductGrid: React.FC<ApparelProductGridProps> = ({
  products,
  categories,
  onEdit,
  onDelete,
  onSelect,
  showAdminControls = false,
}) => {
  const { filteredProducts, filters, updateFilters } = useProductFiltering(products);

  const renderCard = (product: ApparelProduct) => (
    <ErrorBoundary
      key={product.id}
      componentName={`ProductCard-${product.id}`}
      fallback={
        <div className="h-full w-full rounded-lg border border-red-200 bg-red-50 p-4">
          <ConnectionErrorAlert 
            message="Failed to render product"
            description="There was an error displaying this product. Please try refreshing the page."
          />
        </div>
      }
    >
      <ProductCard
        product={product}
        onEdit={onEdit}
        onDelete={onDelete}
        onSelect={onSelect}
        showAdminControls={showAdminControls}
      />
    </ErrorBoundary>
  );

  const renderListItem = (product: ApparelProduct) => (
    <ErrorBoundary
      key={product.id}
      componentName={`ProductListItem-${product.id}`}
      fallback={
        <div className="w-full rounded-lg border border-red-200 bg-red-50 p-4">
          <ConnectionErrorAlert 
            message="Failed to render product"
            description="There was an error displaying this product. Please try refreshing the page."
          />
        </div>
      }
    >
      <ProductListItem
        product={product}
        onEdit={onEdit}
        onDelete={onDelete}
        onSelect={onSelect}
        showAdminControls={showAdminControls}
      />
    </ErrorBoundary>
  );

  return (
    <div className="space-y-8">
      <ErrorBoundary
        componentName="ProductFilterControls"
        fallback={
          <ConnectionErrorAlert 
            message="Failed to load filters"
            description="There was an error loading the product filters. Please try refreshing the page."
          />
        }
      >
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200/50">
          <ProductFilterControls
            categories={categories}
            filters={filters}
            onFiltersChange={updateFilters}
            showAdminControls={showAdminControls}
          />
        </div>
      </ErrorBoundary>
      
      <div className="min-h-[400px]">
        <ViewSwitcher
          items={filteredProducts}
          storageKey="apparel-view-mode"
          renderCard={renderCard}
          renderListItem={renderListItem}
          cardContainerClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8"
          listContainerClassName="flex flex-col gap-5"
        />
      </div>
    </div>
  );
};

export default ApparelProductGrid;
